import { Clock, Film, Image as ImageIcon, MapPinOff, Play } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { get, type Asset, type Me, type Member, type Trip } from "../lib/api.ts";
import { localDayOf, tripDays, type TripDay } from "../lib/days.ts";
import {
  bucketCounts,
  bucketLast,
  routeLines,
  routeColor,
  sliceUntil,
  toPointFeatures,
  tracksBetween,
  travelledLines,
  type MapAsset,
  type Track,
} from "../lib/mapdata.ts";
import { useApi } from "../lib/useApi.ts";
import { useThumbs } from "../lib/thumbs.ts";
import { Card, EmptyState, ICON, PageState, reason } from "../ui/index.ts";
import { MapDays, type MapDay } from "./MapDays.tsx";
import { MediaCard } from "./media/MediaCard.tsx";
import { MediaLibrary, type LibraryTab } from "./media/MediaLibrary.tsx";

// WebGL map code loads only on this page, as it does on the traveller's map.
const MapView = lazy(() => import("./MapView.tsx").then((m) => ({ default: m.MapView })));

/**
 * The editor's map.
 *
 * An editor did not go on the trip. The only two things they know about any recording are where
 * it was taken and when, so this page answers both at once and then lets them look at the
 * recording properly. It is the editor's home (`landingSection`) and the only map they get.
 *
 * Three readings, and one clock under all of them. The map says where everyone walked; the grid
 * says what was shot; the card says what one recording is. The timeline is what they agree on:
 * move it and all three change together.
 *
 * Two panels under one strip of days. The map is one panel and the trip's library the other,
 * side by side, each scrolling on its own, so the map and the clock stay on screen while the
 * tiles are browsed.
 *
 * The recording being read arrives as a drawer over the library, in from the right. It is a
 * picture, where it was taken and however long the conversation has grown — more than fits under
 * a grid, and parked at the foot of the panel it was something an editor had to scroll down to
 * find. There is no scrim behind it on purpose: the map, the clock and the hour's filmstrip stay
 * lit and live, so a pin or a tile swaps what the drawer is reading without it having to be
 * closed first. Only the library is covered, and the card's own close is the way back to it.
 *
 * The clock is a day and a moment in it. The strip of days sits above both panels because it
 * governs both: it is the trip's own shape — each tab says how much was filmed that day, so the
 * overview is in the navigation rather than in a summary nobody can act on — and it is the
 * page's one answer to "which day am I looking at". The library used to keep a second strip of
 * its own, which could sit on Tuesday under a map reading Thursday. The knob then moves through
 * the day that is open: everyone's path is drawn as far as the knob and no further, and the
 * hour's filmstrip holds what had been shot by then.
 * A width to choose was the wrong offer. "Six hours of what" is the question an editor cannot
 * answer when they arrive, and a day is the unit the footage was actually shot in.
 *
 * The grid is the biggest of the three and comes last, because picking the usable shot is the
 * work — the map is how you narrow to it, not what you stare at. That is the whole difference
 * from the traveller's map, which replays a day the traveller already remembers.
 */

/** The viewer's own zone decides where a day starts, as it does everywhere else in the app. */
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** An editor reads the whole trip at once, so the route is drawn at the cheaper resolution. */
const TRACK_SIMPLIFY_M = 10;
/** The bars behind the knob: one per hour of the day being read. */
const DAY_BARS = 24;
/** What one press of an arrow key moves the knob. A minute is finer than any camera's clock. */
const KNOB_STEP_MS = 60_000;
/**
 * How many tiles are drawn at once. Each is a thumbnail grant, so this is a budget rather than a
 * preference; moving the knob back is how you see the rest, and the grid says so.
 */
const MAX_TILES = 120;

type Histogram = {
  from: string | null;
  to: string | null;
  counts: { start: string; count: number; located: number }[];
};

/**
 * Every map asset for this trip, in one read (bounded).
 *
 * Deliberately not shared with `MapPage`: that page filters by contributor and confidence and
 * re-reads on every change, and importing its loader would pull the traveller's whole map —
 * dial, day strip and all — into an editor's bundle for the sake of ten lines.
 */
async function loadMapAssets(base: string): Promise<MapAsset[]> {
  const out: MapAsset[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 10; page++) {
    const r: { items: MapAsset[]; nextCursor: string | null } = await get(
      `${base}/map-assets?limit=1000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    );
    out.push(...r.items);
    cursor = r.nextCursor;
    if (!cursor) break;
  }
  return out;
}

/** `15:36` — the minute alone, for a label that has already said which day it means. */
const fmtTime = (t: number) =>
  new Date(t).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

/** `14 Oct, 15:36` — the day and the minute, in the reader's own locale. */
const fmt = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

export function EditorMapPage({ trip, me }: { trip: Trip; me: Me }) {
  const base = `/v1/trips/${trip.id}`;
  const [assets, setAssets] = useState<{ items: MapAsset[] } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * The moment being read, in the day being read: this page's whole clock.
   *
   * Null until the trip's span is known. An editor arrives not knowing what was shot, so the
   * knob lands at the end of the last day that holds anything — a complete day — and narrowing
   * is what they do once they know where to look.
   */
  const [playhead, setPlayhead] = useState<number | null>(null);
  /** The recording being read, and the recording itself once it has been fetched. */
  const [openId, setOpenId] = useState<string | null>(null);
  /**
   * Which half of the library is showing. Held here rather than in the address bar: the library
   * is a panel on this page, and an editor's shell carries no media or tags section to point at.
   */
  const [libTab, setLibTab] = useState<LibraryTab>("media");
  /**
   * The tag folder open in the library, if any. Held here for the same reason the tab is: a
   * folder is a page of its own everywhere else, and following that link from here would put a
   * wall of tags where the map, the clock and the hour's footage had been.
   */
  const [libItem, setLibItem] = useState<string | null>(null);
  const [open, setOpen] = useState<Asset | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  /** Bumped when the card changes the recording, so it re-reads rather than drawing stale tags. */
  const [nonce, setNonce] = useState(0);
  /**
   * The drawer, and whatever had focus when it opened.
   *
   * The drawer is not modal — there is nothing behind it to dim and the rest of the page keeps
   * working — but it does arrive over the library, so focus goes into it and comes back out to
   * whatever opened it. Held as the element rather than an id because a recording is opened from
   * three places: a tile in the hour, a tile in the library, and a pin on the map.
   */
  const drawer = useRef<HTMLElement | null>(null);
  const cameFrom = useRef<HTMLElement | null>(null);

  const members = useApi<{ items: Member[] }>(`${base}/members`);
  // Read for its `from`/`to` alone — the trip's span of record, which is not capped the way the
  // ten pages of recordings above are. The bars this page draws are one day's, and a day is
  // counted here from what is already in hand rather than asked for again.
  const histogram = useApi<Histogram>(`${base}/map-histogram`);
  // One whole-trip read at overview resolution: scrubbing slices what is already in hand rather
  // than putting a PostGIS query behind every drag of the slider.
  const tracks = useApi<{ tracks: Track[] }>(`${base}/tracks?simplify=${TRACK_SIMPLIFY_M}`);

  const reload = () => {
    setLoading(true);
    loadMapAssets(base)
      .then((items) => {
        setAssets({ items });
        setError(null);
      })
      .catch((e: Error) => setError(e))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [base]);

  const items = useMemo(() => assets?.items ?? [], [assets]);

  const range = useMemo(() => {
    const times = items.map((a) => Date.parse(a.captureAt)).filter(Number.isFinite);
    const from = histogram.data?.from ? Date.parse(histogram.data.from) : Math.min(...times);
    const to = histogram.data?.to ? Date.parse(histogram.data.to) : Math.max(...times);
    return Number.isFinite(from) && Number.isFinite(to) && to > from ? { from, to } : null;
  }, [items, histogram.data]);

  /** Every recording's instant, which is what the days and the bars are counted from. */
  const times = useMemo(
    () => items.map((a) => Date.parse(a.captureAt)).filter(Number.isFinite),
    [items],
  );
  /** The trip's days, each with what it holds. The strip of tabs is this page's overview. */
  const days = useMemo(
    () => (range ? tripDays(times, range.from, range.to, TIME_ZONE) : []),
    [times, range?.from, range?.to],
  );
  /** The day the knob is in. */
  const today: TripDay | null = useMemo(() => {
    if (playhead === null) return null;
    const day = localDayOf(playhead, TIME_ZONE);
    return days.find((d) => d.day === day) ?? null;
  }, [days, playhead]);

  /**
   * Where the knob lands: the end of the last day that holds anything. Also the recovery, for a
   * knob left in a day that a reload no longer has — a moment outside every day would otherwise
   * leave the page with a clock it cannot read.
   *
   * Nothing is landed on until something has actually been recorded. The trip's span arrives
   * from the histogram and the recordings from a paged read, so for a moment the page knows how
   * long the trip is but not what is in it; landing then parks the knob at the end of a day that
   * is empty only because the footage has not turned up yet, and since that is a real moment in
   * a real day the recovery below then keeps it there.
   */
  useEffect(() => {
    const filmed = days.filter((d) => d.count > 0);
    if (filmed.length === 0) return;
    setPlayhead((p) => {
      if (p !== null && days.some((d) => p >= d.from && p < d.to)) return p;
      const last = filmed[filmed.length - 1]!;
      return last.last ?? last.to - 1;
    });
  }, [days]);

  /**
   * The day being read, up to the knob: what the map pins, what the grid lists and what the
   * readout counts, all the same recordings. Up to the knob rather than the whole day, because
   * a clock that only filtered the route would be two clocks — the editor would see a path that
   * stopped at noon under a grid that had already given away the evening.
   */
  const shown = useMemo(() => {
    if (playhead === null || today === null) return [];
    return items.filter((a) => {
      const t = Date.parse(a.captureAt);
      return t >= today.from && t <= playhead;
    });
  }, [items, today?.from, playhead]);

  const allTracks = tracks.data?.tracks ?? [];
  // Clipped here rather than asked for, for the reason above: the whole trip is already loaded.
  const dayTracks = useMemo(
    () => (today ? tracksBetween(allTracks, today.from, today.to - 1) : []),
    [tracks.data, today?.from, today?.to],
  );
  /** The whole day, drawn faintly: the shape the knob is moving through. */
  const ghost = useMemo(() => routeLines(dayTracks), [dayTracks]);
  // Keyed on the slice indices rather than on the knob itself: dragging moves it every frame,
  // and the geometry only changes when a fix is actually crossed.
  const keptKey =
    playhead === null ? "" : dayTracks.map((t) => sliceUntil(t.points, playhead)).join(",");
  /** How far each person had walked by the knob — the reading the knob exists to move. */
  const travelled = useMemo(
    () => (playhead === null ? null : travelledLines(dayTracks, playhead)),
    [keptKey, dayTracks],
  );
  const points = useMemo(() => toPointFeatures(shown), [shown]);

  /**
   * The day in `DAY_BARS` slices, each with what was filmed in it and when the last of it was.
   *
   * One cut of the day for three readings: the bars behind the knob, the hour the strip below is
   * holding, and the hour a bar hands the knob when it is picked. They were two cuts a
   * millisecond apart, which agreed about every hour of every day and would not have been worth
   * finding out about on the day they stopped.
   *
   * Measured from the day's own start rather than from a wall clock, so on a day that gains or
   * loses an hour these are the day in 24 — which is what the editor is looking at.
   */
  const slices = useMemo(() => {
    if (today === null) return [];
    // The day's own span, divided whole. Counted over a millisecond less than the day — which is
    // what it took to keep the closing instant out of the last bar — the boundaries came out a
    // millisecond early, and a clock that shows minutes reported every one of them as the minute
    // before: an hour of footage labelled "04:59 PM – 05:59 PM". The recordings are narrowed to
    // the day first instead, so the inclusive end can be the day's end and match nothing.
    const span = today.to - today.from;
    const mine = times.filter((t) => t >= today.from && t < today.to);
    const counts = bucketCounts(mine, today.from, today.to, DAY_BARS);
    const lasts = bucketLast(mine, today.from, today.to, DAY_BARS);
    return counts.map((count, i) => ({
      from: today.from + (span * i) / DAY_BARS,
      to: today.from + (span * (i + 1)) / DAY_BARS,
      count,
      last: lasts[i] ?? null,
    }));
  }, [times, today?.from, today?.to]);
  const tallest = Math.max(1, ...slices.map((s) => s.count));

  /** The slice the knob is standing in. */
  const hour = useMemo(
    () => slices.find((s) => playhead !== null && playhead >= s.from && playhead < s.to) ?? null,
    [slices, playhead],
  );

  /**
   * Picking an hour off the histogram. The knob lands on the last thing filmed in that hour, or
   * on its final instant when nothing was — the same answer a day tab gives for a whole day, so
   * the hour arrives complete: its recordings are pinned and every path is drawn through it.
   */
  const pickHour = (s: { to: number; last: number | null }) => {
    if (today === null) return;
    setPlayhead(s.last ?? Math.max(today.from, Math.floor(s.to) - 1));
  };

  /**
   * What was recorded in that hour: the strip under the timeline. Newest first and capped, as
   * the grid was — an hour of a burst can still be hundreds of frames.
   */
  const tiles = useMemo(
    () =>
      hour === null
        ? []
        : items
            .filter((a) => {
              const t = Date.parse(a.captureAt);
              return t >= hour.from && t < hour.to;
            })
            .slice(-MAX_TILES)
            .reverse(),
    [items, hour?.from, hour?.to],
  );
  const thumbIds = useMemo(
    () => tiles.filter((a) => a.thumbReady !== false).map((a) => a.assetId),
    [tiles],
  );
  const thumbs = useThumbs(trip.id, thumbIds);

  /**
   * The recording being read. The map holds a read model — a place, a time, a kind — and the card
   * needs the recording itself: its components, tags, hearts and conversation. So it is fetched
   * when one is opened rather than for every pin on screen.
   */
  useEffect(() => {
    if (openId === null) {
      setOpen(null);
      setOpenError(null);
      return;
    }
    let live = true;
    setOpenError(null);
    get<Asset>(`${base}/assets/${openId}`)
      .then((a) => {
        if (live) setOpen(a);
      })
      .catch((e: Error) => {
        if (live) setOpenError(reason(e));
      });
    return () => {
      live = false;
    };
  }, [base, openId, nonce]);

  /**
   * A recording is opened from a tile or from a pin on the map; both land here. The way back is
   * remembered only on the way in, so swapping recordings while the drawer is open — which is
   * what the live map and filmstrip behind it are for — does not overwrite it with something
   * inside the drawer, or with nothing at all when the swap came from a pin.
   */
  const openRecording = (assetId: string) => {
    if (openId === null) {
      const active = document.activeElement;
      cameFrom.current = active instanceof HTMLElement ? active : null;
    }
    setOpenId(assetId);
  };

  /**
   * Put the recording away and hand focus back, if what opened it is still on the page — the tile
   * may have gone with the hour the knob has since moved out of.
   */
  const closeRecording = () => {
    setOpenId(null);
    const back = cameFrom.current;
    cameFrom.current = null;
    if (back?.isConnected) back.focus();
  };

  // Focus moves into the drawer as it arrives, and only as it arrives: a swap from a pin or a
  // tile changes what the drawer holds, and must not drag focus out of the card being read.
  const reading = openId !== null;
  useEffect(() => {
    if (reading) drawer.current?.focus({ preventScroll: true });
  }, [reading]);

  const name = (id: string) =>
    members.data?.items.find((m) => m.userId === id)?.displayName ?? "Someone";
  const describeTile = (a: MapAsset) =>
    `${a.kind === "VIDEO" ? "Video" : "Photo"} by ${name(a.contributorId)}, ${fmt(
      Date.parse(a.captureAt),
    )}`;

  const unlocated = shown.filter((a) => !a.location).length;
  const noun = (n: number) => `${n} ${n === 1 ? "recording" : "recordings"}`;
  /**
   * What the clock has admitted. A day read to its end says what it holds; a day read part way
   * says how much of it that is, because the number on its tab is the whole day's and the two
   * disagreeing without explanation would read as a bug.
   */
  const countLabel =
    today === null
      ? ""
      : shown.length === today.count
        ? `${noun(today.count)} on this day`
        : `${shown.length} of ${noun(today.count)} so far`;

  /**
   * What the strip says when it has nothing to show, and only then. An hour that holds footage
   * is already saying so — in the tiles, each of which carries its own time — and a line
   * counting them above the row was a sentence the reader had to be told once and then read on
   * every move of the knob, at the price of a row the map would rather have.
   */
  const hourEmpty =
    hour === null
      ? "No dated footage yet"
      : `Nothing recorded between ${fmtTime(hour.from)} and ${fmtTime(hour.to)}`;

  /** The tabs. Picking a day lands on the end of it, so a day arrives complete. */
  const dayTabs: MapDay[] = useMemo(
    () => days.map((d) => ({ day: d.day, at: d.last ?? d.to - 1, count: d.count })),
    [days],
  );

  const mapBox = (
    <div className="map-box">
      <Suspense fallback={<div className="map skeleton" />}>
        <MapView
          points={points}
          routes={travelled ?? ghost}
          ghost={travelled ? ghost : null}
          selectedId={openId}
          onSelect={openRecording}
          fitPadding={48}
        />
      </Suspense>
      {dayTracks.some((t) => t.points.length > 0) && (
        <ul className="legend plain" aria-label="Routes">
          {/* Indexed over every track of the day, including the ones it has no line for: the
              colours are handed out by position, so filtering here would recolour the legend. */}
          {dayTracks.map((t, i) =>
            t.points.length === 0 ? null : (
              <li key={t.userId}>
                <span
                  className="swatch"
                  style={{ background: routeColor(t, i) }}
                  aria-hidden="true"
                />
                {t.displayName}
              </li>
            ),
          )}
          <li>
            <span className="dot solid" aria-hidden="true" /> File GPS
          </li>
          <li>
            <span className="dot hollow" aria-hidden="true" /> Estimated
          </li>
        </ul>
      )}
    </div>
  );

  /** The clock the whole page is told by: which day, and how far into it. */
  const timeline = (
    <div className="editor-timeline">
      <p className="small editor-readout" aria-live="polite">
        <Clock {...ICON} aria-hidden="true" />
        <strong>{playhead === null ? "No dated footage yet" : fmt(playhead)}</strong>
        {countLabel ? ` · ${countLabel}` : ""}
        {unlocated > 0 ? ` · ${unlocated} without a place` : ""}
      </p>

      {/*
       * The day's shape, and the way into any hour of it: each bar is a slice of the day, drawn
       * as how much was filmed in it, and picking one moves the knob there.
       *
       * Out of the tab order and unannounced on purpose. This is the same reading the knob gives
       * and reaches the same places, and the knob is a slider — it takes the keyboard already,
       * a minute to an arrow key. Twenty-four more tab stops would be the same control twice,
       * so these are a shortcut for a pointer rather than a second way through the day.
       */}
      <div className="histogram" aria-hidden="true">
        {slices.map((s) => (
          <button
            key={s.from}
            type="button"
            tabIndex={-1}
            className="bar"
            title={`${fmtTime(s.from)} – ${fmtTime(s.to)} · ${
              s.count === 0 ? "nothing recorded" : noun(s.count)
            }`}
            onClick={() => pickHour(s)}
          >
            <span
              style={{ height: `${Math.round((s.count / tallest) * 100)}%` }}
              className={playhead !== null && s.from <= playhead ? "in" : ""}
            />
          </button>
        ))}
      </div>

      <label className="field">
        <span className="sr">Time on this day</span>
        <input
          type="range"
          className="knob"
          min={today ? today.from : 0}
          max={today ? today.to - 1 : 1}
          step={KNOB_STEP_MS}
          value={playhead ?? 0}
          disabled={today === null}
          onChange={(e) => setPlayhead(Number(e.target.value))}
          aria-valuetext={
            playhead === null ? "No dated footage yet" : `${fmt(playhead)} · ${countLabel}`
          }
        />
      </label>
    </div>
  );

  /**
   * Escape puts the recording away. Guarded, because the knob is a slider and a composer is
   * somebody typing — neither should lose what it is doing to a keystroke meant for the page.
   */
  const onKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (openId === null || e.key !== "Escape") return;
    if ((e.target as HTMLElement).closest("input, textarea, select, [contenteditable='true']")) {
      return;
    }
    closeRecording();
  };

  /** One square in the hour's strip. */
  const face = (a: MapAsset) => {
    const url = thumbs[a.assetId];
    const current = openId === a.assetId;
    return (
      <button
        type="button"
        className="tile-face"
        aria-pressed={current}
        {...(current ? { "aria-current": true } : {})}
        aria-label={describeTile(a)}
        onClick={() => openRecording(a.assetId)}
      >
        {url ? (
          <img src={url} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="tile-blank" aria-hidden="true">
            {a.kind === "VIDEO" ? (
              <Film size={20} strokeWidth={1.5} />
            ) : (
              <ImageIcon size={20} strokeWidth={1.5} />
            )}
          </span>
        )}
        {a.kind === "VIDEO" && url && (
          <span className="tile-play" aria-hidden="true">
            <Play size={14} strokeWidth={2} fill="currentColor" />
          </span>
        )}
      </button>
    );
  };

  return (
    <PageState
      data={assets}
      error={error}
      loading={loading}
      reload={reload}
      isEmpty={(d) => d.items.length === 0 && allTracks.length === 0}
      empty={
        <EmptyState
          icon={<MapPinOff size={24} strokeWidth={1.5} />}
          title="Nothing to place on the map yet"
          body="Recordings appear here once the trip has uploaded them. Their place comes from the file’s GPS or from the traveller’s shared route."
        />
      }
    >
      {() => (
        <div className="editor-map" onKeyDown={onKeys}>
          {/*
           * The trip's days, across the top of both panels. One strip, and everything under it
           * follows: the clock, the paths on the map, the hour's filmstrip and the library's
           * grid. Inside the map's panel it read as the map's own control, while the library
           * answered the same question for itself a few inches to the right.
           */}
          {dayTabs.length > 0 && (
            <MapDays
              className="day-tabs editor-days"
              days={dayTabs}
              current={today?.day ?? ""}
              onPick={setPlayhead}
            />
          )}

          <section className="card editor-canvas" aria-label="Map and timeline">
            {mapBox}
            {timeline}
            {/*
             * The hour the knob is standing in, laid out along the bottom of the map's own panel.
             * The timeline says when and the map says where; this says what — the three readings
             * of one moment, side by side, which is the whole argument for the page.
             */}
            <section className="hour-strip" aria-label="Recordings in this hour">
              {/* Drawn whether or not the hour holds anything: the row is what keeps the map
                  above it from resizing every time the knob crosses an empty hour. */}
              <ul className="filmstrip plain">
                {tiles.map((a) => (
                  <li key={a.assetId} className={`tile${openId === a.assetId ? " checked" : ""}`}>
                    {face(a)}
                  </li>
                ))}
              </ul>
              {/* In the row rather than above it, so an hour that holds nothing can say so
                  without the hours that hold something paying for the sentence. */}
              {tiles.length === 0 && <p className="hour-empty muted small">{hourEmpty}</p>}
            </section>
          </section>

          {/*
           * The trip's whole library, and over it whichever recording is being read.
           *
           * The same two views a traveller has on their phone — Media and Tags — rather than a
           * second grid that would disagree with the first about what a day is or what a tag
           * means. The day it shows is the day the strip above is on, so it is the trip's library
           * narrowed by the page's own clock rather than a library with a mind of its own.
           */}
          <section className="card editor-media" aria-label="The trip's media">
            <div className="media-browse">
              <MediaLibrary
                trip={trip}
                me={me}
                tab={libTab}
                itemId={libItem}
                day={today?.day ?? null}
                onTab={(t) => {
                  setLibTab(t);
                  // Leaving Tags closes whatever was open in it: coming back to a folder the
                  // reader left by another door is not what the tab offers.
                  setLibItem(null);
                }}
                onItem={setLibItem}
                onOpen={(a) => openRecording(a.id)}
              />
            </div>

            {openId !== null && (
              <section
                className="media-drawer"
                aria-label="The open recording"
                ref={drawer}
                tabIndex={-1}
              >
                {openError ? (
                  <Card>
                    <p role="alert" className="error">
                      {openError}
                    </p>
                    <button type="button" className="button" onClick={() => setNonce((n) => n + 1)}>
                      Try again
                    </button>
                  </Card>
                ) : open ? (
                  <MediaCard
                    trip={trip}
                    asset={open}
                    members={members.data?.items ?? []}
                    thumb={thumbs[open.id] ?? null}
                    live
                    active
                    onClose={closeRecording}
                    onChanged={() => setNonce((n) => n + 1)}
                  />
                ) : (
                  <p className="muted small" style={{ margin: 0 }}>
                    Opening…
                  </p>
                )}
              </section>
            )}
          </section>
        </div>
      )}
    </PageState>
  );
}
